import { useMemo } from "react";

import type { ColumnDef } from "@tanstack/react-table";

import type { components } from "../../api/schema";
import { shortResourceId } from "../../lib/shellNavigation";
import type { ListQueryResult } from "../../lib/useListQuery";
import {
  Button,
  Drawer,
  EmptyState,
  Inline,
  InlineError,
  Skeleton,
  Stack,
  Text,
} from "../../ui";
import { DateCell } from "../shared/DateCell";
import { NameWithId } from "../shared/NameWithId";
import { PaginatedTableSection } from "../shared/PaginatedTableSection";
import { type ScenarioMemberOption } from "./OrderedScenarioMembersEditor";
import {
  ScenarioSetEditor,
  type ScenarioSetFormSubmission,
} from "./ScenarioSetEditor";
import {
  ScenarioSetRowActions,
  type ScenarioSetRowActionHandlers,
} from "./ScenarioSetRowActions";

type ScenarioSetResponse = components["schemas"]["ScenarioSetResponse"];

export type ScenarioSetDrawerState =
  | { mode: "create" }
  | { mode: "edit"; scenarioSet: ScenarioSetResponse }
  | { mode: "loading" }
  | { message: string; mode: "error" };

export interface ScenarioSetsListViewProps {
  // Agent display names for the unscoped Agent column.
  agentNamesById?: ReadonlyMap<string, string>;
  deleteError: string | null;
  deleteScenarioSetId: string | null;
  drawer: ScenarioSetDrawerState | null;
  hasNextScenarioPage: boolean;
  isDeletePending: boolean;
  isFetchingNextScenarioPage: boolean;
  isHydratingSelectedScenarios: boolean;
  isSavePending: boolean;
  onCancelDelete: () => void;
  onCloseEditor: () => void;
  onConfirmDelete: (scenarioSet: ScenarioSetResponse) => void;
  onLoadMoreScenarios: () => void;
  onOpenEdit: (scenarioSetId: string) => void;
  onSave: (submission: ScenarioSetFormSubmission) => Promise<void>;
  onStartDelete: (scenarioSet: ScenarioSetResponse) => void;
  saveError: string | null;
  scenarioOptions: ScenarioMemberOption[];
  scenarioOptionsError?: string;
  scenarioSets: ListQueryResult<ScenarioSetResponse>;
  scopedAgentId?: string;
}

function scenarioSetColumns(
  handlers: ScenarioSetRowActionHandlers & {
    agentNamesById?: ReadonlyMap<string, string>;
    showAgentColumn: boolean;
  },
): Array<ColumnDef<ScenarioSetResponse>> {
  const agentName = (scenarioSet: ScenarioSetResponse) =>
    handlers.agentNamesById?.get(scenarioSet.agent_id) ??
    shortResourceId(scenarioSet.agent_id);

  const columns: Array<ColumnDef<ScenarioSetResponse>> = [
    {
      header: "Name",
      id: "name",
      accessorFn: (scenarioSet) => scenarioSet.display_name,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.id}
          idLabel="Scenario set ID"
          name={row.original.display_name}
          strong
        />
      ),
    },
  ];

  if (handlers.showAgentColumn) {
    columns.push({
      header: "Agent",
      id: "agent",
      accessorFn: (scenarioSet) => agentName(scenarioSet),
      cell: ({ row }) => (
        <NameWithId
          id={row.original.agent_id}
          idLabel="Agent ID"
          name={agentName(row.original)}
        />
      ),
    });
  }

  columns.push(
    {
      header: "Scenarios",
      id: "scenarios",
      accessorFn: (scenarioSet) => scenarioSet.members?.length ?? 0,
    },
    {
      header: "Updated",
      id: "updated",
      accessorFn: (scenarioSet) => scenarioSet.updated_at,
      cell: ({ row }) => <DateCell value={row.original.updated_at} />,
    },
    {
      header: "Actions",
      id: "actions",
      cell: ({ row }) => (
        <ScenarioSetRowActions handlers={handlers} scenarioSet={row.original} />
      ),
    },
  );

  return columns;
}

export function ScenarioSetsListView({
  agentNamesById,
  deleteError,
  deleteScenarioSetId,
  drawer,
  hasNextScenarioPage,
  isDeletePending,
  isFetchingNextScenarioPage,
  isHydratingSelectedScenarios,
  isSavePending,
  onCancelDelete,
  onCloseEditor,
  onConfirmDelete,
  onLoadMoreScenarios,
  onOpenEdit,
  onSave,
  onStartDelete,
  saveError,
  scenarioOptions,
  scenarioOptionsError,
  scenarioSets,
  scopedAgentId,
}: ScenarioSetsListViewProps) {
  const showAgentColumn = scopedAgentId === undefined;
  const columns = useMemo(
    () =>
      scenarioSetColumns({
        agentNamesById,
        deleteError,
        deleteScenarioSetId,
        isDeletePending,
        onCancelDelete,
        onConfirmDelete,
        onOpenEdit,
        onStartDelete,
        showAgentColumn,
      }),
    [
      agentNamesById,
      deleteError,
      deleteScenarioSetId,
      isDeletePending,
      onCancelDelete,
      onConfirmDelete,
      onOpenEdit,
      onStartDelete,
      showAgentColumn,
    ],
  );
  return (
    <>
      <PaginatedTableSection
        ariaLabel="Scenario sets"
        columns={columns}
        emptyState={
          <EmptyState
            description="Order matters. Scenarios run top to bottom."
            title="No scenario sets"
          />
        }
        enableSorting
        errorMessage={scenarioSets.errorMessage}
        getRowId={(scenarioSet) => scenarioSet.id}
        hasNextPage={scenarioSets.hasNextPage}
        isFetchingNextPage={scenarioSets.isFetchingNextPage}
        isPending={scenarioSets.isPending}
        items={scenarioSets.items}
        listStateKey={scenarioSets.listStateKey}
        onLoadMore={scenarioSets.fetchNextPage}
        onRowClick={(row) => {
          onOpenEdit(row.original.id);
        }}
        skeletonLabel="Loading scenario sets"
        virtual={{ fill: true, interactiveRows: true }}
      />
      {drawer ? (
        <Drawer
          ariaLabel={
            drawer.mode === "create"
              ? "Create scenario set"
              : "Edit scenario set"
          }
          isOpen
          onDismiss={onCloseEditor}
        >
          {drawer.mode === "loading" || isHydratingSelectedScenarios ? (
            <Skeleton
              label="Loading scenario set"
              rows={5}
              shape="detail-field"
            />
          ) : drawer.mode === "error" ? (
            <Stack gap={4}>
              <Text variant="caps">Edit scenario set</Text>
              <InlineError>{drawer.message}</InlineError>
              <Inline>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={onCloseEditor}
                >
                  Close
                </Button>
              </Inline>
            </Stack>
          ) : (
            <ScenarioSetEditor
              error={saveError}
              hasNextScenarioPage={hasNextScenarioPage}
              isFetchingNextScenarioPage={isFetchingNextScenarioPage}
              isPending={isSavePending}
              scenarioOptions={scenarioOptions}
              scenarioOptionsError={scenarioOptionsError}
              scenarioSet={drawer.mode === "edit" ? drawer.scenarioSet : null}
              onCancel={onCloseEditor}
              onLoadMoreScenarios={onLoadMoreScenarios}
              onSubmit={onSave}
            />
          )}
        </Drawer>
      ) : null}
    </>
  );
}
